import { ITyreIdentifierField } from '@wyz/types';
import {
  Badge,
  BrandLoader,
  Flex,
  Icon,
  Img,
  SeasonIcon,
  Text,
  Tooltip,
} from '@wyz/ui';
import {
  DEFAULT_TIRE_BRAND_LOGO,
  DEFAULT_TIRE_BRAND_WIDTH,
  getBrandLogoUrl,
  getTyreIdentifiers,
  isEmptyValue,
} from '@wyz/utils';
import { Trans, useConfig, useI18n } from '~/common/context';
import { SEGMENT_NAME } from '~/common/translators';
import { ISegmentName } from '~/common/types';
import { IQuoteItemInfo, IQuoteTyreOffer } from '~/purchase/types';

type Props = {
  tyreOffer: IQuoteTyreOffer;
  tyre: IQuoteItemInfo['tyre'];
  tyreIdentifierFields?: ITyreIdentifierField[];
  pointOfSaleView: boolean;
};

function QuoteRowProductInfo({
  tyreOffer,
  tyre,
  tyreIdentifierFields,
  pointOfSaleView,
}: Props) {
  const { cdnUrl, withCdnUrl } = useConfig();
  const { t } = useI18n();

  const tyreImage = tyre.image ? `/common/tyres${tyre.image}` : undefined;
  const identifiers = getTyreIdentifiers(
    {
      cai: tyreOffer.tyre.supplierCode,
      constructorReference: tyreOffer.tyre.constructorReference,
      alternativeReference: tyreOffer.tyre.alternativeReference,
    },
    tyreIdentifierFields,
  );

  const segmentTranslationKey =
    SEGMENT_NAME[tyreOffer.tyre.segment.name as ISegmentName];

  const wetGripValue = isEmptyValue(tyreOffer.tyre.labelling?.wetGrip)
    ? '-'
    : tyreOffer.tyre.labelling.wetGrip;
  const rollingResistanceValue = isEmptyValue(
    tyreOffer.tyre.labelling?.rollingResistance,
  )
    ? '-'
    : tyreOffer.tyre.labelling.rollingResistance;
  const noisePerformanceValue = isEmptyValue(
    tyreOffer.tyre.labelling?.noisePerformance,
  )
    ? '-'
    : tyreOffer.tyre.labelling.noisePerformance;

  return (
    <div className='p-2 h-100'>
      <Flex alignItems='center'>
        <Flex direction='column' alignItems='center' className='gap-2'>
          <Tooltip label={tyre.brand}>
            <Img
              src={getBrandLogoUrl(tyreOffer.tyre.brand.name)}
              baseUrl={cdnUrl}
              data-cy={`product-info-brand-${tyre.ean}`}
              defaultImage={DEFAULT_TIRE_BRAND_LOGO}
              alt={tyre.brand}
              width={DEFAULT_TIRE_BRAND_WIDTH.sm}
              loader={BrandLoader}
              style={{ objectFit: 'scale-down', maxHeight: 40 }}
            />
          </Tooltip>
          {segmentTranslationKey && (
            <div className='rounded border border-2 border-neutral-grey-5 py-1 px-2'>
              <Text heading='xs' weight='semi-bold' color='tertiary-bold'>
                <Trans i18nKey={segmentTranslationKey} />
              </Text>
            </div>
          )}
        </Flex>
        <Img
          height={90}
          src={tyreImage}
          baseUrl={cdnUrl}
          defaultImage={withCdnUrl('/img/tire-sample.png')}
          alt={tyre.profile ?? tyreOffer.tyre.profile.design1}
          data-cy={`quote-row-tyre-image-${tyreOffer.tyre.ean}`}
          style={{ maxWidth: 130, objectFit: 'contain' }}
        />
        <Flex className='gap-2 flex-1' alignItems='center'>
          {!pointOfSaleView && (
            <Flex direction='column' className='gap-1'>
              <Flex alignItems='center' className='gap-2'>
                <Icon icon='showers-line' size='xs' />
                <Text heading='sm' weight='bold' color='secondary'>
                  {wetGripValue}
                </Text>
              </Flex>
              <Flex alignItems='center' className='gap-2'>
                <Icon icon='gas-station-line' size='xs' />
                <Text heading='sm' weight='bold' color='secondary'>
                  {rollingResistanceValue}
                </Text>
              </Flex>
              <Flex alignItems='center' className='gap-2'>
                <Icon icon='volume-down-line' size='xs' />
                <Text heading='sm' weight='bold' color='secondary'>
                  {noisePerformanceValue}
                </Text>
              </Flex>
              <Flex alignItems='center' className='gap-2'>
                <SeasonIcon value={tyreOffer.tyre.season.name} size='xs' />
                {tyreOffer.tyre.season.pmsf === 'Y' && (
                  <Icon icon='mountain-flake' size='xs' />
                )}
              </Flex>
            </Flex>
          )}

          <div className='flex-1 flex-wrap'>
            <Text
              heading='sm'
              weight='bold'
              color='tertiary-bold'
              className='mb-1 text-uppercase '
            >
              {tyreOffer.tyre.profile.design1}
            </Text>
            <Badge
              bg='light'
              pill
              size='xxs'
              className='px-1 py-2 fw-bold text-secondary '
            >
              {tyreOffer.tyre.dimension.fullSize}
            </Badge>
            <div className='d-flex flex-column gap-1 mt-2 flex-wrap'>
              <Text heading='xxs' weight='semi-bold' color='secondary-gray'>
                {t('app.common.product.ean_value', {
                  value: tyreOffer.tyre.ean,
                })}
              </Text>
              {identifiers.alternativeReference && (
                <Text heading='xxs' weight='semi-bold' color='secondary-gray'>
                  {t('app.common.product.reference_value', {
                    value: identifiers.alternativeReference,
                  })}
                </Text>
              )}
              {identifiers.constructorReference && (
                <Text heading='xxs' weight='semi-bold' color='secondary-gray'>
                  {t('app.common.product.constructor_reference_value', {
                    value: identifiers.constructorReference,
                  })}
                </Text>
              )}
              {identifiers.cai && (
                <Text heading='xxs' weight='semi-bold' color='secondary-gray'>
                  {t('app.common.product.cai_value', {
                    value: identifiers.cai,
                  })}
                </Text>
              )}
            </div>
          </div>
        </Flex>
      </Flex>
    </div>
  );
}

export default QuoteRowProductInfo;
